import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { likeCollectionApi } from '../api/user/likeCollection/LikeCollectionApi';
import type { EngagementRequestOptions } from '../api/user/engagementTypes';
import { notifyViewOnlyAccess } from '../utils/writeAccess';
import { RECIPE_ENGAGEMENT_VIEW_ONLY_MESSAGE } from './useRecipeEngagementWriteAccess';

const STORAGE_VER = 'v1';

export type Collection = {
  id: string;
  name: string;
  recipeIds: string[];
  createdAt: string;
};

const loadFromStorage = (key: string): Set<string> => {
  if (typeof window === 'undefined') return new Set();
  try {
    const stored = localStorage.getItem(key);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
};

const saveToStorage = (key: string, value: Set<string>) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(Array.from(value)));
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
  }
};

const loadCollections = (key: string): Collection[] => {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveCollectionsToKey = (key: string, collections: Collection[]) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(collections));
  } catch (error) {
    console.error('Failed to save collections to localStorage:', error);
  }
};

function storageKeys(profileId: string) {
  const p = `ma_rec_eng_${STORAGE_VER}_${profileId}`;
  return {
    liked: `${p}_liked`,
    saved: `${p}_saved`,
    collections: `${p}_collections`,
  };
}

type ColRow = {
  id?: string | number;
  name?: string;
  recipeIds?: string[];
  recipe_ids?: string[];
  recipes?: Array<{ id?: string | number; uuid?: string }>;
};

function collectionsToSavedIds(data: ColRow[]): Set<string> {
  const allSavedIds = new Set<string>();
  data.forEach((col) => {
    const ids = col.recipeIds || col.recipe_ids || [];
    ids.forEach((id: string | number) => allSavedIds.add(String(id)));
    if (col.recipes) {
      col.recipes.forEach((r) => {
        if (r.uuid) allSavedIds.add(String(r.uuid));
        if (r.id != null) allSavedIds.add(String(r.id));
      });
    }
  });
  return allSavedIds;
}

function applyCollectionsPayload(
  raw: unknown,
  setCollections: Dispatch<SetStateAction<Collection[]>>,
  setSavedRecipes: Dispatch<SetStateAction<Set<string>>>,
) {
  const data = raw as ColRow[];
  const normalized: Collection[] = data.map((col) => {
    const recipeIds = new Set<string>();
    for (const id of col.recipeIds || col.recipe_ids || []) {
      if (id != null && id !== '') recipeIds.add(String(id));
    }
    for (const r of col.recipes || []) {
      if (r?.uuid) recipeIds.add(String(r.uuid));
      if (r?.id != null && r.id !== '') recipeIds.add(String(r.id));
    }
    return {
      id: String(col.id ?? ''),
      name: col.name || '',
      recipeIds: Array.from(recipeIds),
      createdAt: new Date().toISOString(),
    };
  });
  setCollections(normalized);
  setSavedRecipes(collectionsToSavedIds(data));
}

/**
 * @param enableAuthSync - fetch likes/collections when authenticated
 * @param actingProfileId - user id for engagement scope (self or family member); required when enableAuthSync
 * @param canWriteEngagement - false for viewer on another profile (API role from family members)
 */
export const useRecipeInteractions = (
  enableAuthSync = true,
  actingProfileId?: string,
  canWriteEngagement = true,
) => {
  const engagementOpts: EngagementRequestOptions | undefined = useMemo(
    () =>
      actingProfileId?.trim()
        ? { profileId: actingProfileId.trim() }
        : undefined,
    [actingProfileId],
  );

  /** Invalidates in-flight profile sync when actingProfileId or auth changes (last run wins). */
  const profileSyncGenerationRef = useRef(0);

  const [likedRecipes, setLikedRecipes] = useState<Set<string>>(
    () => new Set(),
  );
  const [savedRecipes, setSavedRecipes] = useState<Set<string>>(
    () => new Set(),
  );
  const [collections, setCollections] = useState<Collection[]>([]);

  // Persist per acting profile (or skip when logged out)
  useEffect(() => {
    if (!enableAuthSync || !actingProfileId?.trim()) return;
    const { liked } = storageKeys(actingProfileId.trim());
    saveToStorage(liked, likedRecipes);
  }, [likedRecipes, enableAuthSync, actingProfileId]);

  useEffect(() => {
    if (!enableAuthSync || !actingProfileId?.trim()) return;
    const { saved } = storageKeys(actingProfileId.trim());
    saveToStorage(saved, savedRecipes);
  }, [savedRecipes, enableAuthSync, actingProfileId]);

  useEffect(() => {
    if (!enableAuthSync || !actingProfileId?.trim()) return;
    const { collections: ck } = storageKeys(actingProfileId.trim());
    saveCollectionsToKey(ck, collections);
  }, [collections, enableAuthSync, actingProfileId]);

  const toggleLike = useCallback(
    async (recipeId: string) => {
      if (!engagementOpts) return;
      if (!canWriteEngagement) {
        notifyViewOnlyAccess(RECIPE_ENGAGEMENT_VIEW_ONLY_MESSAGE);
        return;
      }

      setLikedRecipes((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(recipeId)) newSet.delete(recipeId);
        else newSet.add(recipeId);
        return newSet;
      });

      try {
        const response = await likeCollectionApi.toggleLike(
          recipeId,
          engagementOpts,
        );
        if (!response.success) {
          setLikedRecipes((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(recipeId)) newSet.delete(recipeId);
            else newSet.add(recipeId);
            return newSet;
          });
          console.error('Failed to toggle like:', response.error);
        }
      } catch (error) {
        setLikedRecipes((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(recipeId)) newSet.delete(recipeId);
          else newSet.add(recipeId);
          return newSet;
        });
        console.error('Error toggling like:', error);
      }
    },
    [engagementOpts, canWriteEngagement],
  );

  const toggleSave = useCallback(
    (recipeId: string) => {
      if (!canWriteEngagement) {
        notifyViewOnlyAccess(RECIPE_ENGAGEMENT_VIEW_ONLY_MESSAGE);
        return;
      }
      setSavedRecipes((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(recipeId)) newSet.delete(recipeId);
        else newSet.add(recipeId);
        return newSet;
      });
    },
    [canWriteEngagement],
  );

  const isLiked = useCallback(
    (recipeId: string) => likedRecipes.has(recipeId),
    [likedRecipes],
  );

  const isSaved = useCallback(
    (recipeId: string) => savedRecipes.has(recipeId),
    [savedRecipes],
  );

  const createCollection = useCallback(
    (name: string) => {
      if (!canWriteEngagement) {
        notifyViewOnlyAccess(RECIPE_ENGAGEMENT_VIEW_ONLY_MESSAGE);
        return '';
      }
      const newCollection: Collection = {
        id: Date.now().toString(),
        name,
        recipeIds: [],
        createdAt: new Date().toISOString(),
      };
      setCollections((prev) => [...prev, newCollection]);
      return newCollection.id;
    },
    [canWriteEngagement],
  );

  const deleteCollection = useCallback(
    async (collectionId: string) => {
      if (!engagementOpts) return;
      if (!canWriteEngagement) {
        notifyViewOnlyAccess(RECIPE_ENGAGEMENT_VIEW_ONLY_MESSAGE);
        return;
      }
      try {
        const response = await likeCollectionApi.deleteCollection(
          collectionId,
          engagementOpts,
        );
        if (response.success) {
          setCollections((prev) =>
            prev.filter((c) => String(c.id) !== String(collectionId)),
          );
          const collectionsResponse =
            await likeCollectionApi.getCollections(engagementOpts);
          if (collectionsResponse.success && collectionsResponse.data) {
            applyCollectionsPayload(
              collectionsResponse.data,
              setCollections,
              setSavedRecipes,
            );
          }
        } else {
          console.error('Failed to delete collection:', response.error);
          const collectionsResponse =
            await likeCollectionApi.getCollections(engagementOpts);
          if (collectionsResponse.success && collectionsResponse.data) {
            applyCollectionsPayload(
              collectionsResponse.data,
              setCollections,
              setSavedRecipes,
            );
          }
        }
      } catch (error) {
        console.error('Error deleting collection:', error);
        const collectionsResponse =
          await likeCollectionApi.getCollections(engagementOpts);
        if (collectionsResponse.success && collectionsResponse.data) {
          applyCollectionsPayload(
            collectionsResponse.data,
            setCollections,
            setSavedRecipes,
          );
        }
      }
    },
    [engagementOpts, canWriteEngagement],
  );

  const addRecipeToCollection = useCallback(
    (collectionId: string, recipeId: string) => {
      if (!canWriteEngagement) {
        notifyViewOnlyAccess(RECIPE_ENGAGEMENT_VIEW_ONLY_MESSAGE);
        return;
      }
      setCollections((prev) =>
        prev.map((c) =>
          c.id === collectionId
            ? {
                ...c,
                recipeIds: c.recipeIds.includes(recipeId)
                  ? c.recipeIds
                  : [...c.recipeIds, recipeId],
              }
            : c,
        ),
      );
    },
    [canWriteEngagement],
  );

  const removeRecipeFromCollection = useCallback(
    (collectionId: string, recipeId: string) => {
      if (!canWriteEngagement) {
        notifyViewOnlyAccess(RECIPE_ENGAGEMENT_VIEW_ONLY_MESSAGE);
        return;
      }
      setCollections((prev) =>
        prev.map((c) =>
          c.id === collectionId
            ? { ...c, recipeIds: c.recipeIds.filter((id) => id !== recipeId) }
            : c,
        ),
      );
    },
    [canWriteEngagement],
  );

  const updateCollectionName = useCallback(
    async (collectionId: string, newName: string) => {
      if (!engagementOpts) return;
      if (!canWriteEngagement) {
        notifyViewOnlyAccess(RECIPE_ENGAGEMENT_VIEW_ONLY_MESSAGE);
        return;
      }
      const trimmed = (newName || '').trim();
      if (!trimmed) return;

      setCollections((prev) =>
        prev.map((c) =>
          String(c.id) === String(collectionId) ? { ...c, name: trimmed } : c,
        ),
      );

      try {
        const response = await likeCollectionApi.updateCollectionName(
          collectionId,
          trimmed,
          engagementOpts,
        );
        if (!response.success) {
          const collectionsResponse =
            await likeCollectionApi.getCollections(engagementOpts);
          if (collectionsResponse.success && collectionsResponse.data) {
            setCollections(collectionsResponse.data as Collection[]);
          }
        }
      } catch (error) {
        console.error('Failed to update collection name:', error);
        const collectionsResponse =
          await likeCollectionApi.getCollections(engagementOpts);
        if (collectionsResponse.success && collectionsResponse.data) {
          setCollections(collectionsResponse.data as Collection[]);
        }
      }
    },
    [engagementOpts, canWriteEngagement],
  );

  const getCollection = useCallback(
    (collectionId: string) => collections.find((c) => c.id === collectionId),
    [collections],
  );

  // Reset + hydrate when profile changes; API is source of truth
  useEffect(() => {
    const syncGeneration = ++profileSyncGenerationRef.current;

    queueMicrotask(() => {
      if (profileSyncGenerationRef.current !== syncGeneration) return;

      if (!enableAuthSync || !actingProfileId?.trim()) {
        setLikedRecipes(new Set());
        setSavedRecipes(new Set());
        setCollections([]);
        return;
      }

      const pid = actingProfileId.trim();
      const keys = storageKeys(pid);
      setLikedRecipes(loadFromStorage(keys.liked));
      setSavedRecipes(loadFromStorage(keys.saved));
      setCollections(loadCollections(keys.collections));

      const syncWithBackend = async () => {
        const stale = () => profileSyncGenerationRef.current !== syncGeneration;
        try {
          const likedResponse = await likeCollectionApi.getLikedRecipes({
            profileId: pid,
          });
          if (stale()) return;

          if (likedResponse.success && likedResponse.data) {
            const apiLikedIds = new Set(
              likedResponse.data.map(
                (r: { id?: string | number; uuid?: string }) =>
                  r.uuid || String(r.id),
              ),
            );
            setLikedRecipes(apiLikedIds);
          }

          const collectionsResponse = await likeCollectionApi.getCollections({
            profileId: pid,
          });
          if (stale()) return;

          if (collectionsResponse.success && collectionsResponse.data) {
            applyCollectionsPayload(
              collectionsResponse.data,
              setCollections,
              setSavedRecipes,
            );
          }
        } catch (error) {
          if (stale()) return;
          console.error('Failed to sync interactions with backend:', error);
        }
      };

      void syncWithBackend();
    });
  }, [enableAuthSync, actingProfileId]);

  const likedCount = likedRecipes.size;
  const savedCount = savedRecipes.size;

  return {
    toggleLike,
    toggleSave,
    isLiked,
    isSaved,
    likedCount,
    savedCount,
    likedRecipeIds: Array.from(likedRecipes),
    savedRecipeIds: Array.from(savedRecipes),
    collections,
    createCollection,
    deleteCollection,
    addRecipeToCollection,
    removeRecipeFromCollection,
    updateCollectionName,
    getCollection,
    setLikedRecipes,
    setSavedRecipes,
  };
};
